import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Send, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { ImageUpload } from "../components/ImageUpload";
import { MediaUpload } from "../components/MediaUpload";
import { CATEGORIES } from "../components/CategoryFilter";
import { api } from "../lib/api";
import { useI18n } from "../i18n";
import { toast } from "sonner";

export default function NewRequestPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "",
    quantity: "",
    budget: "",
    buyer_name: "",
    email: "",
    phone: "",
    media: [],
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        quantity: parseInt(form.quantity) || 1,
      };
      const res = await api.post("/requests", payload);
      toast.success(t("posted"));
      navigate(`/r/${res.data.id}`);
    } catch (e) {
      toast.error(t("error_submit"));
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit =
    form.title.trim() &&
    form.description.trim() &&
    form.category &&
    form.quantity &&
    form.budget.trim() &&
    form.buyer_name.trim() &&
    !submitting;

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 md:px-10 py-6 sm:py-10 md:py-16">
      <button
        data-testid="back-to-board"
        onClick={() => navigate("/")}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("back_to_board")}
      </button>

      <div className="mb-10">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3">
          {t("nav_post")}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
          {t("form_new_request")}
        </h1>
        <p className="mt-4 text-base text-muted-foreground max-w-xl leading-relaxed">
          {t("form_new_request_desc")}
        </p>
      </div>

      <form onSubmit={submit} className="space-y-8 rounded-md border border-border bg-white p-6 md:p-10">
        <div className="space-y-2">
          <Label htmlFor="title" className="text-xs tracking-[0.15em] uppercase font-semibold">
            {t("title_label")}
          </Label>
          <Input
            id="title"
            data-testid="req-title-input"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder={t("title_placeholder")}
            className="h-11 rounded-md"
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="description" className="text-xs tracking-[0.15em] uppercase font-semibold">
            {t("description_label")}
          </Label>
          <Textarea
            id="description"
            data-testid="req-description-input"
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder={t("description_placeholder")}
            rows={4}
            className="rounded-md resize-none"
            required
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label className="text-xs tracking-[0.15em] uppercase font-semibold">
              {t("category_label")}
            </Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="req-category-select" className="h-11 rounded-md bg-white">
                <SelectValue placeholder={t("category_placeholder")} />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`cat-option-${c.replace(/\s+/g, "-").toLowerCase()}`}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="quantity" className="text-xs tracking-[0.15em] uppercase font-semibold">
              {t("quantity_label")}
            </Label>
            <Input
              id="quantity"
              data-testid="req-quantity-input"
              type="number"
              min="1"
              value={form.quantity}
              onChange={(e) => set("quantity", e.target.value)}
              placeholder="10000"
              className="h-11 rounded-md font-mono-brand"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="budget" className="text-xs tracking-[0.15em] uppercase font-semibold">
              {t("budget_label")}
            </Label>
            <Input
              id="budget"
              data-testid="req-budget-input"
              value={form.budget}
              onChange={(e) => set("budget", e.target.value)}
              placeholder={t("budget_placeholder")}
              className="h-11 rounded-md"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="buyer_name" className="text-xs tracking-[0.15em] uppercase font-semibold">
              {t("buyer_name_label")}
            </Label>
            <Input
              id="buyer_name"
              data-testid="req-buyer-name-input"
              value={form.buyer_name}
              onChange={(e) => set("buyer_name", e.target.value)}
              placeholder={t("buyer_name_placeholder")}
              className="h-11 rounded-md"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs tracking-[0.15em] uppercase font-semibold">
              {t("email_label")}
            </Label>
            <Input
              id="email"
              data-testid="req-email-input"
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder={t("email_placeholder")}
              className="h-11 rounded-md"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-xs tracking-[0.15em] uppercase font-semibold">
              {t("phone_label")}
            </Label>
            <Input
              id="phone"
              data-testid="req-phone-input"
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              placeholder={t("phone_placeholder")}
              className="h-11 rounded-md font-mono-brand"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs tracking-[0.15em] uppercase font-semibold">
            {t("photo_label")}
          </Label>
          <MediaUpload
            media={form.media}
            onChange={(m) => set("media", m)}
            testId="req-media-upload"
          />
        </div>

        <div className="pt-4 border-t border-border">
          <Button
            type="submit"
            data-testid="submit-request-btn"
            disabled={!canSubmit}
            size="lg"
            className="w-full sm:w-auto rounded-md gap-2 h-12 px-8"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> {t("posting")}
              </>
            ) : (
              <>
                <Send className="h-4 w-4" /> {t("submit_request")}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
